import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { CapitalCall } from "@/types";

export function useCapitalCalls(fundId?: string) {
  return useQuery({
    queryKey: ["capital_calls", fundId ?? "none"],
    enabled: !!fundId,
    queryFn: async () => {
      const { data } = await apiClient.get<CapitalCall[]>(`/funds/${fundId}/capital-calls`);
      return data;
    },
  });
}

export function useCreateCapitalCall(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { call_date: string; percentage: number }) => {
      const { data } = await apiClient.post<CapitalCall>(`/funds/${fundId}/capital-calls`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_calls", fundId] }),
  });
}

export function useUpdateCapitalCall(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; call_date?: string; percentage?: number }) => {
      const { data } = await apiClient.patch<CapitalCall>(`/funds/${fundId}/capital-calls/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_calls", fundId] }),
  });
}

export function useDeleteCapitalCall(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/funds/${fundId}/capital-calls/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_calls", fundId] }),
  });
}
